// ThingsToDo.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

// === HERO BACKGROUND ===
import heroBg from '../assets/Sonic/85.jpeg';

// === ACTIVITY IMAGES ===
import ayurveda from '../assets/ThingsToDo/Ayurvedic-Therapies-For-Stress-Relief.webp';
import diving from '../assets/ThingsToDo/unadiving1.jpg';
import elephantRide from '../assets/ThingsToDo/elephant-back-riding.jpg';
import fishing from '../assets/ThingsToDo/S4.jpg';
import ballooning from '../assets/ThingsToDo/hot-air-baloon.jpg';
import jeepSafari from '../assets/ThingsToDo/2409696_orig.jpg';
import whiteWaterRafting from '../assets/ThingsToDo/rafting7.jpg';
import riverSafari from '../assets/ThingsToDo/river-safari-slider1.jpg';
import snorkeling from '../assets/ThingsToDo/Snorkelers_2.jpg';
import teaTasting from '../assets/ThingsToDo/Tea-Tasting.avif';
import templeVisit from '../assets/ThingsToDo/Buddhist-Temples-in-Sri-Lanka.jpg';
import wildlifeTour from '../assets/ThingsToDo/protect-the-environment-on-your-sri-lanka-wildlife-safari-header.jpg';

// === MOTION VARIANTS ===
const sectionVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: 'easeOut' } },
};

const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.1, duration: 0.6, ease: 'easeOut' }
    })
};

const ThingsToDo = () => {
    const { t } = useTranslation('thingstodo');
    const navigate = useNavigate();

    const activities = [
        { image: ayurveda, title: t('ayurveda'), route: '/ayurveda', emoji: '🧘', category: 'Wellness', color: '#FF6B9D' },
        { image: diving, title: t('diving'), route: '/diving', emoji: '🤿', category: 'Water Sports', color: '#00D4FF' },
        { image: elephantRide, title: t('elephantRide'), route: '/elephant-ride', emoji: '🐘', category: 'Wildlife', color: '#FFB347' },
        { image: fishing, title: t('fishing'), route: '/fishing', emoji: '🎣', category: 'Adventure', color: '#4A90E2' },
        { image: ballooning, title: t('ballooning'), route: '/ballooning', emoji: '🎈', category: 'Adventure', color: '#FF9A56' },
        { image: jeepSafari, title: t('jeepSafari'), route: '/jeep-safari', emoji: '🚙', category: 'Wildlife', color: '#2ECC71' },
        { image: whiteWaterRafting, title: t('whiteWaterRafting'), route: '/white-water-rafting', emoji: '🚣', category: 'Water Sports', color: '#3498DB' },
        { image: riverSafari, title: t('riverSafari'), route: '/river-safari', emoji: '⛵', category: 'Nature', color: '#1ABC9C' },
        { image: snorkeling, title: t('snorkeling'), route: '/snorkeling', emoji: '🏊', category: 'Water Sports', color: '#E74C3C' },
        { image: teaTasting, title: t('teaTasting'), route: '/tea-tasting', emoji: '🍵', category: 'Cultural', color: '#27AE60' },
        { image: templeVisit, title: t('templeVisit'), route: '/temple-visit', emoji: '🏯', category: 'Cultural', color: '#9B59B6' },
        { image: wildlifeTour, title: t('wildlifeSafari'), route: '/wildlife-safari', emoji: '🦁', category: 'Wildlife', color: '#E67E22' },
    ];

    return (
        <div className="bg-gradient-to-br from-[#001F3F] via-[#000C1F] to-[#001F3F] min-h-screen overflow-hidden">
            {/* HERO SECTION */}
            <div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative flex h-[70vh] w-full items-center justify-center font-lato md:h-[60vh] lg:h-[85vh] overflow-hidden"
            >
                <img
                    src={heroBg}
                    alt={t('heroTitle')}
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/70 to-[#001F3F]/95" />

                {/* Animated background elements */}
                <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-[#0074D9]/20 to-transparent rounded-full blur-3xl opacity-60 -translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-[#7FDBFF]/20 to-transparent rounded-full blur-3xl opacity-60 translate-x-1/2 translate-y-1/2"></div>

                <div className="container relative z-10 flex flex-col items-center justify-center pt-[60px] px-4">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                        className="text-center"
                    >
                        <div className="inline-block mb-6 px-6 py-2 rounded-full bg-gradient-to-r from-[#0074D9]/20 to-[#7FDBFF]/20 border border-[#7FDBFF]/40">
                            <span className="text-[#7FDBFF] text-sm font-semibold tracking-wider">EXPLORE & DISCOVER</span>
                        </div>

                        <h1 className="text-center text-[35px] font-bold leading-tight text-white font-konkhmer md:text-[65px] lg:text-[85px] drop-shadow-2xl">
                            {t('heroTitle')}
                        </h1>

                        <p className="mt-8 max-w-3xl mx-auto font-inknut text-lg font-medium text-gray-100 md:text-2xl leading-relaxed">
                            {t('heroSubtitle')}
                        </p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.4 }}
                            className="mt-10"
                        >
                            <a
                                href="#activities"
                                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-gradient-to-r from-[#0074D9] to-[#7FDBFF] text-white font-bold text-lg hover:shadow-2xl hover:scale-105 transition-all duration-300"
                            >
                                Start Exploring <span className="ml-1">→</span>
                            </a>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            {/* ACTIVITIES SECTION */}
            <section
                id="activities"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={sectionVariants}
                className="py-24 lg:py-32 relative"
            >
                {/* Decorative elements */}
                <div className="absolute top-20 left-0 w-72 h-72 bg-gradient-to-br from-[#0074D9]/10 to-transparent rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-tl from-[#7FDBFF]/10 to-transparent rounded-full blur-3xl"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16 lg:mb-24"
                    >
                        <div className="inline-block mb-6 px-6 py-2 rounded-full bg-gradient-to-r from-[#0074D9]/20 to-[#7FDBFF]/20 border border-[#7FDBFF]/40">
                            <span className="text-[#7FDBFF] text-sm font-semibold tracking-wider">12 AMAZING EXPERIENCES</span>
                        </div>

                        <h2 className="font-konkhmer text-4xl font-bold uppercase md:text-5xl lg:text-6xl text-white mb-6">
                            {t('sectionTitle')}
                        </h2>

                        <div className="flex justify-center gap-3 mb-2">
                            <motion.div
                                initial={{ width: 0 }}
                                whileInView={{ width: '120px' }}
                                transition={{ duration: 0.8, delay: 0.2 }}
                                className="h-1.5 bg-gradient-to-r from-[#0074D9] to-[#7FDBFF] rounded-full"
                            ></motion.div>
                            <motion.div
                                initial={{ width: 0 }}
                                whileInView={{ width: '80px' }}
                                transition={{ duration: 0.8, delay: 0.3 }}
                                className="h-1.5 bg-gradient-to-r from-[#7FDBFF] to-[#0074D9] rounded-full"
                            ></motion.div>
                        </div>

                        <p className="mt-8 text-gray-300 text-lg max-w-2xl mx-auto leading-relaxed">
                            Discover unforgettable adventures and immersive cultural experiences across the beautiful island of Sri Lanka
                        </p>
                    </motion.div>

                    {/* Activities Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 lg:gap-8">
                        {activities.map((item, idx) => {
                            return (
                                <motion.div
                                    key={idx}
                                    custom={idx}
                                    variants={cardVariants}
                                    initial="hidden"
                                    whileInView="visible"
                                    viewport={{ once: true, amount: 0.3 }}
                                    whileHover={{ y: -12 }}
                                    whileTap={{ scale: 0.95 }}
                                    className="group relative h-[500px] md:h-[450px] lg:h-[550px] rounded-2xl overflow-hidden cursor-pointer shadow-2xl"
                                    onClick={() => navigate(item.route)}
                                >
                                    {/* Background Image */}
                                    <img
                                        src={item.image}
                                        alt={item.title}
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-120"
                                        loading="lazy"
                                    />

                                    {/* Gradient Overlays */}
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/40 to-transparent opacity-80 group-hover:opacity-90 transition-opacity duration-500"></div>
                                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/80"></div>

                                    {/* Emoji Badge */}
                                    <motion.div
                                        initial={{ opacity: 0, scale: 0.5 }}
                                        whileInView={{ opacity: 1, scale: 1 }}
                                        transition={{ delay: idx * 0.1 + 0.2 }}
                                        className="absolute top-6 right-6 z-20 p-3 rounded-full bg-gradient-to-br from-white/20 to-white/10 backdrop-blur-md border border-white/30 group-hover:scale-110 transition-transform duration-300 text-3xl"
                                    >
                                        {item.emoji}
                                    </motion.div>

                                    {/* Category Badge */}
                                    <div className="absolute top-6 left-6 z-20">
                                        <span
                                            className="inline-block px-4 py-1.5 rounded-full text-white text-xs font-bold uppercase tracking-wider shadow-lg"
                                            style={{ backgroundColor: item.color + '80' }}
                                        >
                                            {item.category}
                                        </span>
                                    </div>

                                    {/* Content Container */}
                                    <div className="absolute inset-0 flex flex-col items-center justify-end p-6 z-10">
                                        {/* Title */}
                                        <motion.h4
                                            initial={{ opacity: 0, y: 20 }}
                                            whileInView={{ opacity: 1, y: 0 }}
                                            transition={{ delay: idx * 0.1 + 0.1 }}
                                            className="font-redHatDisplay text-2xl md:text-xl lg:text-2xl font-bold text-white text-center drop-shadow-2xl mb-4 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-[#7FDBFF] group-hover:to-[#0074D9] group-hover:bg-clip-text transition-all duration-300"
                                        >
                                            {item.title}
                                        </motion.h4>

                                        {/* Divider */}
                                        <div className="w-12 h-1 bg-gradient-to-r from-transparent via-white to-transparent mb-4 opacity-50 group-hover:opacity-100 transition-opacity"></div>

                                        {/* Button */}
                                        <motion.button
                                            whileHover={{ scale: 1.05 }}
                                            whileTap={{ scale: 0.95 }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(item.route);
                                            }}
                                            className="px-6 py-3 rounded-full font-bold text-white text-sm md:text-base flex items-center gap-2 transition-all duration-300 shadow-xl hover:shadow-2xl group-hover:translate-y-0 translate-y-2"
                                            style={{
                                                background: `linear-gradient(135deg, ${item.color}, ${item.color}dd)`,
                                            }}
                                        >
                                            {t('viewMore')}
                                            <span className="ml-1 group-hover:translate-x-1 transition-transform inline-block">→</span>
                                        </motion.button>
                                    </div>

                                    {/* Hover Shine Effect */}
                                    <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 opacity-0 group-hover:opacity-1 transition-opacity duration-500 -translate-x-full group-hover:translate-x-full" style={{ animation: 'none' }}></div>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* CTA SECTION */}
            <section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="py-20 lg:py-28 relative"
            >
                <div className="absolute inset-0 bg-gradient-to-r from-[#0074D9]/5 to-[#7FDBFF]/5"></div>

                <div className="container mx-auto px-4 relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                        className="text-center max-w-3xl mx-auto"
                    >
                        <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                            {t('ctaTitle')}
                        </h3>
                        <p className="text-gray-300 text-lg mb-10 leading-relaxed">
                            {t('ctaDescription')}
                        </p>
                        <motion.a
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            href="https://wa.me/94767584594?text=Hello%20Sonic%20Tours!%20I%20am%20interested%20in%20booking%20an%20experience%20."
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-3 px-10 py-4 rounded-full bg-gradient-to-r from-[#0074D9] via-[#7FDBFF] to-[#0074D9] text-white font-bold text-lg hover:shadow-2xl transition-all duration-300"
                        >
                            {t('ctaButton')} <span className="ml-1">→</span>
                        </motion.a>
                    </motion.div>
                </div>
            </section>
        </div>
    );
};

export default ThingsToDo;